/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import type {
	JsmListIssueSearchInput,
	JsmListDefaultProps,
} from '@jira/servicedesk__jsm-list/src/JsmListIssueSearchInput.types.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import { fg } from '@atlassian/jira-feature-gating';
import parameters from './__generated__/ServicedeskListListQuery$parameters.ts';
import type { ServicedeskListListQuery } from './__generated__/ServicedeskListListQuery.graphql.ts';

type ListEntryPointComponent = EntryPointComponent<
	{
		jsmListDataQuery: ServicedeskListListQuery;
	},
	{},
	{},
	{
		issueSearchInput: JsmListIssueSearchInput;
		cloudId: string;
		projectKey: string;
		defaultProps: JsmListDefaultProps;
	}
>;

export const listEntryPoint: EntryPoint<ListEntryPointComponent, EntryPointRouteParams> =
	createEntryPoint({
		root: JSResourceForInteraction<ListEntryPointComponent>(
			/* webpackChunkName: "async-servicedesk-list", jiraSpaEntry: "async-servicedesk-list" */ './ServicedeskListList.tsx',
		),
		getPreloadProps: ({
			context: { match },
			tenantContext: { cloudId },
		}: EntryPointRouteParams) => {
			const boardId = match.params.boardId;
			const boardAri = `ari:cloud:jira-software:${cloudId}:board/${boardId}`;
			const projectKey = match.params.projectKey ?? '';

			const issueSearchInput = {
				customInput: {
					jiraSoftwareInput: {
						jiraEntityId: boardAri,
						additionalJql: '',
					},
				},
			};

			const fieldSetsInput = fg('jsm_list_minor_bug_fixes_and_analytic_event')
				? {
						viewInput: {
							namespace: 'JSM_ISSUE_NAVIGATOR',
							viewId: 'list_default',
							filterId: null,
						},
					}
				: {
						viewInput: {
							namespace: 'JSM_ISSUE_NAVIGATOR',
							filterId: null,
						},
					};

			const defaultProps = {
				first: 50,
				last: null,
				before: null,
				after: null,
				options: { issueKey: null },
				viewConfigInput: {
					staticViewInput: {
						isHierarchyEnabled: false,
						isGroupingEnabled: false,
						isHideDoneEnabled: false,
					},
				},
				filterId: null,
				namespace: 'JSM_ISSUE_NAVIGATOR',
				amountOfColumns: 500,
				groupBy: null,
				viewQueryInput: null,
				shouldQueryHasChildrenWithCount: false,
				fieldSetsInput,
				scope: {
					operationScope: 'NIN_PROJECT' as const,
					projectKey,
				},
				fieldSetsScope: {
					operationScope: 'NIN_PROJECT' as const,
					projectKey,
				},
				boardId: boardAri,
				viewId: 'list_default',
			};

			return {
				queries: {
					jsmListDataQuery: {
						options: {
							fetchPolicy: 'store-and-network' as const,
						},
						parameters,
						variables: {
							cloudId,
							issueSearchInput,
							projectKey,
							...defaultProps,
						},
					},
				},
				extraProps: {
					cloudId,
					projectKey,
					issueSearchInput,
					defaultProps,
				},
			};
		},
	});
